/**
 * Produkt-Slider Navigation (Pfeile + horizontaler Track).
 * Custom Element, damit die Init auch nach Theme-Editor-Re-Renders greift.
 */
class ProductFeaturedSliderCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #previousButton = null;
  /** @type {HTMLButtonElement | null} */
  #nextButton = null;
  /** @type {number} */
  #columns = 1;
  /** @type {number} */
  #columnsMobile = 2;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-featured-slider-track]');
    this.#previousButton = this.querySelector('[data-featured-slider-nav="previous"]');
    this.#nextButton = this.querySelector('[data-featured-slider-nav="next"]');
    this.#columns = Number(this.dataset.columns || 1) || 1;
    this.#columnsMobile = Number(this.dataset.columnsMobile || 2) || 2;

    if (!this.#track) return;

    this.#previousButton?.addEventListener('click', this.#onPrevious);
    this.#nextButton?.addEventListener('click', this.#onNext);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#updateNavButtons());
      this.#resizeObserver.observe(this.#track);
    }

    requestAnimationFrame(() => {
      this.#updateNavButtons();
      window.setTimeout(() => this.#updateNavButtons(), 250);
    });
  }

  disconnectedCallback() {
    this.#previousButton?.removeEventListener('click', this.#onPrevious);
    this.#nextButton?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.#resizeObserver?.disconnect();
  }

  #onPrevious = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#scrollByPage(-1);
  };

  #onNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#scrollByPage(1);
  };

  #onScroll = () => {
    this.#updateNavButtons();
  };

  /** @returns {number} */
  #visibleColumns() {
    return window.matchMedia('(min-width: 750px)').matches ? this.#columns : this.#columnsMobile;
  }

  #scrollByPage(direction) {
    const track = this.#track;
    if (!track) return;

    const firstItem = track.querySelector('.product-featured-slider__item');
    if (!firstItem) return;

    const styles = getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const itemWidth = firstItem.getBoundingClientRect().width;
    const distance = (itemWidth + gap) * this.#visibleColumns();

    if (distance <= 0) return;

    track.scrollBy({
      left: direction * distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });

    window.setTimeout(() => this.#updateNavButtons(), 350);
  }

  #updateNavButtons() {
    const track = this.#track;
    if (!track) return;

    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    const canScroll = maxScroll > 2;
    const atStart = track.scrollLeft <= 1;
    const atEnd = !canScroll || track.scrollLeft >= maxScroll - 1;

    if (this.#previousButton) {
      this.#previousButton.disabled = atStart;
    }

    if (this.#nextButton) {
      this.#nextButton.disabled = atEnd;
    }
  }
}

if (!customElements.get('product-featured-slider-carousel')) {
  customElements.define('product-featured-slider-carousel', ProductFeaturedSliderCarousel);
}
